import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { featuredAnime, animeByCategory, articleContent } from '@/mocks/anime';

const articleDetailImages: Record<string, string> = {
  'a1': 'https://readdy.ai/api/search-image?query=Anime%20style%20illustration%20of%20summer%20festival%20with%20fireworks%20and%20lanterns%2C%20warm%20coral%20evening%20sky%2C%20crowded%20festive%20street%2C%20clean%20flat%20illustration%20style%2C%20cinematic%20wide%20shot&width=1200&height=600&seq=article-detail-01&orientation=landscape',
  'a2': 'https://readdy.ai/api/search-image?query=Anime%20style%20illustration%20of%20traditional%20Japanese%20castle%20at%20night%20with%20full%20moon%2C%20warm%20amber%20lighting%2C%20mysterious%20atmosphere%2C%20clean%20flat%20illustration%20style%2C%20cinematic%20wide%20shot&width=1200&height=600&seq=article-detail-02&orientation=landscape',
  'a3': 'https://readdy.ai/api/search-image?query=Chinese%20donghua%20style%20illustration%20of%20celestial%20palace%20above%20clouds%2C%20warm%20golden%20lighting%2C%20epic%20majestic%20view%2C%20clean%20flat%20illustration%20style%2C%20cinematic%20wide%20shot&width=1200&height=600&seq=article-detail-03&orientation=landscape',
  'a4': 'https://readdy.ai/api/search-image?query=Anime%20style%20illustration%20of%20cozy%20library%20reading%20room%20with%20warm%20lamp%20light%2C%20soft%20coral%20and%20cream%20tones%2C%20peaceful%20atmosphere%2C%20clean%20flat%20illustration%20style%2C%20cinematic%20wide%20shot&width=1200&height=600&seq=article-detail-04&orientation=landscape',
  'a5': 'https://readdy.ai/api/search-image?query=Anime%20style%20illustration%20of%20epic%20battle%20scene%20with%20energy%20effects%20and%20dramatic%20lighting%2C%20warm%20orange%20and%20red%20dynamic%20composition%2C%20clean%20flat%20illustration%20style%2C%20cinematic%20wide%20shot&width=1200&height=600&seq=article-detail-05&orientation=landscape',
};

const allAnime = [
  ...featuredAnime,
  ...(animeByCategory.classic || []),
  ...(animeByCategory.movie || []),
  ...(animeByCategory.ova || []),
];

function findAnime(id: string) {
  return allAnime.find((a) => a.id === id);
}

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();

  const anime = id ? findAnime(id) : undefined;
  const article = id ? articleContent[id] : undefined;

  if (!id) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-foreground-500">文章不存在</p>
      </div>
    );
  }

  const isArticlePage = !!article;
  const title = isArticlePage ? article.title : anime?.title || '详情';
  const description = isArticlePage
    ? article.content[0]?.slice(0, 160) || ''
    : anime?.description || '';
  const coverImage = articleDetailImages[id] || articleDetailImages['a1'];

  const contentSchema = isArticlePage
    ? {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: article.title,
        description: article.content[0]?.slice(0, 160) || '',
        datePublished: article.date,
        author: { '@type': 'Person', name: article.author },
        publisher: {
          '@type': 'Organization',
          name: '奶片动漫',
        },
      }
    : {
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: anime?.title,
        description: anime?.description,
        category: anime?.category,
      };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://naipiananime.com' },
      { '@type': 'ListItem', position: 2, name: '分类浏览', item: 'https://naipiananime.com/category' },
      { '@type': 'ListItem', position: 3, name: title },
    ],
  };

  return (
    <>
      <SEOHead
        title={title}
        description={description}
        canonicalUrl={`https://naipiananime.com/article/${id}`}
        ogType="article"
        schema={{ ...contentSchema, ...breadcrumbSchema } as unknown as Record<string, unknown>}
      />

      <main>
        <div className="max-w-4xl mx-auto px-4 md:px-6 pt-24 pb-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '分类浏览', href: '/category' },
              { label: title },
            ]}
          />
        </div>

        <article className="max-w-4xl mx-auto px-4 md:px-6 pb-16">
          <div className="relative w-full aspect-[2/1] rounded-lg overflow-hidden bg-background-200 mb-8">
            <img
              src={coverImage}
              alt={title}
              title={title + ' - 奶片动漫'}
              className="w-full h-full object-cover"
            />
          </div>

          {isArticlePage ? (
            <>
              <div className="mb-8">
                <div className="flex items-center gap-2 mb-3">
                  <span className="px-3 py-1 bg-accent-100 text-accent-700 text-xs rounded-full font-medium whitespace-nowrap">
                    {article.category}
                  </span>
                  <span className="text-sm text-foreground-400">{article.date}</span>
                  <span className="text-sm text-foreground-400">· {article.author}</span>
                </div>
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-4">
                  {article.title}
                </h1>
                <div className="flex flex-wrap gap-2">
                  {article.tags.map((tag) => (
                    <span key={tag} className="px-2.5 py-1 bg-secondary-100 text-secondary-700 text-xs rounded-full font-medium whitespace-nowrap">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              <div className="prose-custom space-y-5">
                {article.content.map((paragraph, index) => (
                  <p key={index} className="text-foreground-700 leading-relaxed text-sm md:text-base">
                    {paragraph}
                  </p>
                ))}
              </div>
            </>
          ) : anime ? (
            <>
              <div className="mb-8">
                <div className="flex items-center gap-2 mb-3">
                  <span className="px-3 py-1 bg-accent-100 text-accent-700 text-xs rounded-full font-medium whitespace-nowrap">
                    {anime.category}
                  </span>
                  <span className="px-3 py-1 bg-primary-100 text-primary-700 text-xs rounded-full font-medium whitespace-nowrap">
                    {anime.rating} 分
                  </span>
                  {anime.status === '连载中' && (
                    <span className="px-3 py-1 bg-primary-500 text-white text-xs rounded-full font-medium whitespace-nowrap">
                      连载中
                    </span>
                  )}
                </div>
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-4">
                  {anime.title}
                </h1>
                <p className="text-sm text-foreground-500 mb-4">{anime.titleEn}</p>

                <div className="flex flex-wrap gap-2 mb-6">
                  {anime.tags.map((tag) => (
                    <span key={tag} className="px-2.5 py-1 bg-secondary-100 text-secondary-700 text-xs rounded-full font-medium whitespace-nowrap">
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 p-5 bg-background-100 rounded-lg mb-6">
                  <div>
                    <p className="text-xs text-foreground-400 mb-1">集数</p>
                    <p className="text-sm font-semibold text-foreground-900">{anime.episodes} 集</p>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-400 mb-1">播放量</p>
                    <p className="text-sm font-semibold text-foreground-900">{anime.views}</p>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-400 mb-1">上映时间</p>
                    <p className="text-sm font-semibold text-foreground-900">{anime.releaseDate}</p>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-400 mb-1">状态</p>
                    <p className="text-sm font-semibold text-foreground-900">{anime.status}</p>
                  </div>
                </div>

                <div className="prose-custom">
                  <h2 className="text-lg font-heading font-bold text-foreground-900 mb-3">剧情简介</h2>
                  <p className="text-foreground-700 leading-relaxed text-sm md:text-base">
                    {anime.description}
                  </p>
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-20">
              <p className="text-foreground-500">内容不存在</p>
              <Link to="/" className="mt-4 inline-block text-primary-500 hover:text-primary-600 text-sm cursor-pointer">
                返回首页
              </Link>
            </div>
          )}
        </article>
      </main>
    </>
  );
}